import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  output,
  inject,
  input,
  computed,
  signal,
} from '@angular/core';

import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  IconComponent,
  RtTooltip,
  ButtonComponent,
  DropdownComponent,
  DropdownItemComponent,
  HeadingComponent,
} from '@core/user-interface-legacy';

import type { InsertPlaceholderInputDirective } from '../insert-placeholder-input.directive';
import { InsertPlaceholderDirective } from '../insert-placeholder.directive';
import { Placeholder, PlaceholderCategory } from '../placeholder';
import { PlaceholderSelectDropdownComponent } from '../placeholder-select-dropdown/placeholder-select-dropdown.component';
import { PlaceholderService } from '../placeholder.service';

@Component({
  selector: 'rt-placeholder-category-button',
  templateUrl: './placeholder-category-button.component.html',
  styleUrls: ['./placeholder-category-button.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    OverlayOriginDirective,
    RtTooltip,
    IconComponent,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    InsertPlaceholderDirective,
    PlaceholderSelectDropdownComponent,
  ],
})
export class PlaceholderCategoryButtonComponent {
  private readonly placeholderService = inject(PlaceholderService);

  public readonly category = input<PlaceholderCategory>(undefined!);
  public readonly activeTarget = input<InsertPlaceholderInputDirective>();
  public readonly active = input<Placeholder>(undefined!);
  public readonly placeholders = input<Placeholder[]>(undefined!);
  public placeholderCategoryTranslationMap =
    this.placeholderService.placeholderCategoryTranslationMap;

  @ViewChild('placeholderSelectDropdown')
  public placeholderSelectDropdown: ConnectedOverlayDirective;

  public select = output<Placeholder>();

  protected selectedSubcategory = signal<PlaceholderCategory | string>('');

  protected categoryPlaceholders = computed(() =>
    this.placeholders().filter(placeholder =>
      placeholder.id !== 'empty'
        ? this.placeholderService.categoryPlaceholdersMap[this.category()]?.includes(placeholder.id)
        : true,
    ),
  );
  protected subcategories = computed(() =>
    (this.placeholderService.subcategoryPlaceholdersMap[this.category()] || []).filter(
      subcategory => {
        const subcategoryPlaceholders =
          this.placeholderService.categoryPlaceholdersMap[subcategory];
        return subcategoryPlaceholders.every(placeholder =>
          this.categoryPlaceholders()
            .map(p => p.id)
            .includes(placeholder),
        );
      },
    ),
  );
  protected noSubcategoriesPlaceholders = computed(() => {
    const subcategoryPlaceholders = this.subcategories().flatMap(
      subcategory => this.placeholderService.categoryPlaceholdersMap[subcategory],
    );
    return this.categoryPlaceholders().filter(placeholder =>
      placeholder.id !== 'empty' ? !subcategoryPlaceholders.includes(placeholder.id) : true,
    );
  });

  protected placeholderSelectDropdownToggle(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.placeholderSelectDropdown.open = !this.placeholderSelectDropdown.open;
  }

  protected withoutBrackets(str: string): string {
    return str.slice(1, -1);
  }

  protected setSubcategory(subcategory: PlaceholderCategory | string = '', event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.selectedSubcategory.set(subcategory);
  }
}
